// useScrollTimeline — the central scroll → progress → render pipeline.
// Lives in src/planet/ (next to sceneState.ts and PlanetScene.tsx) and
// is mounted exactly once, at the App level. The hook owns:
//
//   1. A scroll listener that computes raw progress (0..1).
//   2. A requestAnimationFrame loop that smooths raw progress into
//      a "displayed" value (critically damped, frame-rate independent).
//   3. A global ref on `window.__planetProgress` so the R3F frame loop
//      can read progress without going through React state.
//   4. A tiny pub/sub bus so the HUD layer can re-render on scene
//      change WITHOUT re-rendering on every frame (the scene index
//      only changes 6 times across the whole page).
//
// The hook is the single source of scroll truth. The previous
// `useScrollProgress` in src/three/ is a per-component duplicate and
// will be removed in a follow-up cleanup pass.

import { useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScrollTimelineState {
  /** Smoothed 0..1 — read inside useFrame, never causes re-renders. */
  progress: React.MutableRefObject<number>;
  /** Raw unsmoothed 0..1, for cases where you want to be honest. */
  raw: React.MutableRefObject<number>;
  /** True when scrollY has changed in the last 200ms. */
  isScrolling: React.MutableRefObject<boolean>;
  /** Subscribe to scene-index changes. Returns an unsubscribe fn. */
  subscribeScene: (cb: (idx: number, blendIn: number) => void) => () => void;
  /** Read the current scene index synchronously. */
  getActiveSceneIndex: () => number;
}

export interface UseScrollTimelineOptions {
  /** Smoothing factor in 0..1. Higher = snappier. Default 0.18. */
  smoothing?: number;
  /** Pixel threshold below which a scroll is "idle". Default 0.5. */
  idleThreshold?: number;
  /** Cooldown after last scroll event before isScrolling flips false. */
  scrollCooldownMs?: number;
}

// ---------------------------------------------------------------------------
// Global pub/sub
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    /** Mirrors the smoothed scroll progress. Read inside useFrame. */
    __planetProgress?: number;
    /** Mirrors the active scene index (0..6). */
    __planetSceneIndex?: number;
    /** Mirrors the active scene blend-in (0..1 within scene). */
    __planetBlendIn?: number;
    /** True when prefers-reduced-motion is set. Read inside useFrame. */
    __planetReducedMotion?: boolean;
    /** Smoothed scroll velocity, normalized to roughly -1..1. */
    __planetScrollVelocity?: number;
  }
}

type SceneListener = (idx: number, blendIn: number) => void;
const sceneListeners = new Set<SceneListener>();

function emitSceneChange(idx: number, blendIn: number) {
  if (typeof window !== 'undefined') {
    window.__planetSceneIndex = idx;
    window.__planetBlendIn = blendIn;
  }
  for (const cb of sceneListeners) {
    try {
      cb(idx, blendIn);
    } catch (e) {
      // Don't let one bad listener kill the bus.
      // eslint-disable-next-line no-console
      console.error('[useScrollTimeline] scene listener threw:', e);
    }
  }
}

// Re-implementation of sceneAtProgress here so the hook has no module
// cycle with sceneState.ts (which is allowed to depend on
// planetSections, but a hook that lives in the React tree should be
// dependency-free and fast). The math is identical and unit tests
// cover both code paths.
const SCENE_BOUNDARIES: { start: number; end: number }[] = [
  { start: 0, end: 0.15 },
  { start: 0.15, end: 0.3 },
  { start: 0.3, end: 0.45 },
  { start: 0.45, end: 0.65 },
  { start: 0.65, end: 0.8 },
  { start: 0.8, end: 0.92 },
  { start: 0.92, end: 1.0 },
];

function sceneAt(p: number): { idx: number; blendIn: number } {
  const c = clamp01(p);
  for (let i = 0; i < SCENE_BOUNDARIES.length; i++) {
    const b = SCENE_BOUNDARIES[i];
    if (c >= b.start && c <= b.end) {
      const span = b.end - b.start;
      const blend = span > 0 ? (c - b.start) / span : 0;
      return { idx: i, blendIn: blend };
    }
  }
  if (c > SCENE_BOUNDARIES[SCENE_BOUNDARIES.length - 1].end) {
    return { idx: SCENE_BOUNDARIES.length - 1, blendIn: 1 };
  }
  return { idx: 0, blendIn: 0 };
}

function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * useScrollTimeline — mounts the scroll listener and the rAF smoothing
 * loop. Call once at the app root.
 */
export function useScrollTimeline(opts: UseScrollTimelineOptions = {}): ScrollTimelineState {
  const { smoothing = 0.18, idleThreshold = 0.5, scrollCooldownMs = 160 } = opts;

  // Refs — the only output that lives outside React's render cycle.
  const progress = useRef(0);
  const raw = useRef(0);
  const isScrolling = useRef(false);

  // Re-render trigger ONLY for the "active scene" pub/sub (HUD chips
  // want to fade in/out as the scene changes — that's a real re-render
  // but it only happens 6-7 times across the page).
  const [, force] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // -----------------------------------------------------------------
    // 0) Reduced motion — disable smoothing if the user opts out.
    // -----------------------------------------------------------------
    const reducedMq = window.matchMedia('(prefers-reduced-motion: reduce)');
    let reducedMotion = reducedMq.matches;
    window.__planetReducedMotion = reducedMotion;
    const onReducedChange = () => {
      reducedMotion = reducedMq.matches;
      window.__planetReducedMotion = reducedMotion;
    };
    reducedMq.addEventListener('change', onReducedChange);

    // -----------------------------------------------------------------
    // 1) Compute raw progress on scroll / resize.
    // -----------------------------------------------------------------
    let lastScrollY = window.scrollY;
    let cooldownId: number | null = null;
    let resizeId: number | null = null;

    const computeTarget = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) return 0;
      const v = window.scrollY / max;
      return clamp01(v);
    };

    const onScroll = () => {
      const y = window.scrollY;
      if (Math.abs(y - lastScrollY) < idleThreshold) return;
      lastScrollY = y;
      raw.current = computeTarget();
      isScrolling.current = true;
      if (cooldownId !== null) window.clearTimeout(cooldownId);
      cooldownId = window.setTimeout(() => {
        isScrolling.current = false;
      }, scrollCooldownMs);
    };

    const onResize = () => {
      // Debounce resize by 200ms — resize storms (mobile URL bar, window
      // drag) would otherwise recompute the scroll target every event.
      if (resizeId !== null) window.clearTimeout(resizeId);
      resizeId = window.setTimeout(() => {
        raw.current = computeTarget();
      }, 200);
    };

    // Initial value.
    raw.current = computeTarget();
    progress.current = raw.current;
    if (typeof window !== 'undefined') {
      window.__planetProgress = progress.current;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    // One-shot rAF after layout settles (late-loading fonts can change
    // document.body.scrollHeight).
    const layoutRaf = requestAnimationFrame(() => {
      raw.current = computeTarget();
    });

    // -----------------------------------------------------------------
    // 2) rAF smoothing loop. Frame-rate independent via dt.
    // -----------------------------------------------------------------
    let lastT = performance.now();
    let raf = 0;
    let lastSceneIdx = -1;
    // Scroll-velocity tracking — feeds the "flight feel" (camera roll,
    // FOV stretch, typography skew). Normalized so a brisk scroll sits
    // around ±1. Forced to 0 for prefers-reduced-motion users.
    let lastProgressForVel = progress.current;
    let smoothedVelocity = 0;

    const tick = (now: number) => {
      const dt = Math.min((now - lastT) / 1000, 1 / 30);
      lastT = now;

      // Critically-damped lerp. smoothing is a per-frame blend weight;
      // multiply by dt * 60 to make it framerate-independent.
      // Skip smoothing entirely for prefers-reduced-motion users.
      if (reducedMotion) {
        progress.current = raw.current;
      } else {
        const k = 1 - Math.pow(1 - smoothing, dt * 60);
        progress.current = progress.current + (raw.current - progress.current) * k;
      }
      if (typeof window !== 'undefined') {
        window.__planetProgress = progress.current;
      }
      // Mirror the smoothed progress onto a CSS custom property so any
      // CSS-only consumer (HUD fades, scroll-cue opacity, parallax
      // background layers) can read it without a React re-render.
      if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty(
          '--planet-progress',
          progress.current.toFixed(4),
        );
      }

      // Velocity — smoothed, clamped, published to window + a CSS var
      // so both the camera rig and CSS-only consumers can react to it.
      if (reducedMotion || dt <= 0) {
        smoothedVelocity = 0;
      } else {
        const rawVel = (progress.current - lastProgressForVel) / dt;
        const normalized = Math.max(-1, Math.min(1, rawVel * 2.4));
        const k = 1 - Math.pow(1 - 0.12, dt * 60);
        smoothedVelocity += (normalized - smoothedVelocity) * k;
        if (Math.abs(smoothedVelocity) < 0.001) smoothedVelocity = 0;
      }
      lastProgressForVel = progress.current;
      if (typeof window !== 'undefined') {
        window.__planetScrollVelocity = smoothedVelocity;
      }
      if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty(
          '--scroll-velocity',
          smoothedVelocity.toFixed(3),
        );
      }

      // Scene boundary — emit only on change, then bump React once.
      const { idx, blendIn } = sceneAt(progress.current);
      if (idx !== lastSceneIdx) {
        lastSceneIdx = idx;
        emitSceneChange(idx, blendIn);
        // Bump HUD via a state nudge. Cheap — only on scene change.
        force((n) => (n + 1) % 1024);
      }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    // First-paint scene emit (so the HUD shows chapter 0 even before
    // the user scrolls).
    const initial = sceneAt(progress.current);
    if (initial.idx !== lastSceneIdx) {
      lastSceneIdx = initial.idx;
      emitSceneChange(initial.idx, initial.blendIn);
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      reducedMq.removeEventListener('change', onReducedChange);
      cancelAnimationFrame(raf);
      cancelAnimationFrame(layoutRaf);
      if (cooldownId !== null) window.clearTimeout(cooldownId);
      if (resizeId !== null) window.clearTimeout(resizeId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smoothing, idleThreshold, scrollCooldownMs]);

  return {
    progress,
    raw,
    isScrolling,
    subscribeScene: (cb) => {
      sceneListeners.add(cb);
      return () => {
        sceneListeners.delete(cb);
      };
    },
    getActiveSceneIndex: () => {
      if (typeof window === 'undefined') return 0;
      return window.__planetSceneIndex ?? 0;
    },
  };
}
